import React, { Component } from 'react';
import { compose } from 'redux';
import { get, isEmpty, intersection } from 'lodash';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import withScrollHack from 'components/HOCs/withScrollHack';
import withMouseControls from 'components/HOCs/withMouseControls';
import isPgPinNeeded from 'utils/is-pg-pin-needed';
import PLAYER_TYPES from 'constants/player-types';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import { DetailsInnerWrapper, DetailsWrapper } from './StyledComponents';
import MainContentSection from './MainContentSection';
import ExtraContentSection from './ExtraContentSection';
import { CONTENT_TYPES } from 'constants/content-types';
import { MISSING_SUBSCRIPTION, NOT_AUTHENTICATED } from 'constants/visibility-types';
import EmptyDataStub from 'components/EmptyDataStub';
import i18n from 'i18n';
import ErrorIcon from 'components/Icons/error';

const BASE_MAIN_SECTION_OFFSET = 0;
const BASE_EXTRA_SECTION_OFFSET = 1440;

class Details extends Component {
  state = {
    pageOffset: 0,
    // Позволяет отключить временно анимацию скролла
    // Полезно перерендера страницы с другим контентом
    noAnimation: false,
  };

  componentDidUpdate(prevProps) {
    const prevId = get(prevProps, ['details', 'data', 'id']);
    const id = get(this.props, ['details', 'data', 'id']);
    if (prevId && prevId !== id) {
      // Hack: just trigger focus on 2nd section in "MainContentSection" (fix scroll issue)
      SpatialNavigationWrapper.focus('@COVER');
      this.setState({ noAnimation: true });
      setTimeout(() => this.setState({ noAnimation: false }));
    }
  }

  saveElementIdAsLastFocused = (selector) => {
    const { lastFocus } = this.props;

    if (lastFocus) {
      lastFocus.save(selector.startsWith('#') ? selector : `#${selector}`);
    }
  };

  onPlay = () => {
    const {
      age,
      isParentalEnabled,
      isLifecellUser,
      details,
      navigateToAuthPage,
      history,
      showEnterPinModal,
      showLifecellPaymentModal,
      navigateToPlayerPage,
      reloadData,
      resetUserData,
    } = this.props;
    // В текущей реализации сезоны грузятся фоном и могут быть не доступны
    // Из-за чего все проверки далее будут не валидны (т.к. это сериал, но сезонов еще нет)
    if (details.isLoading) return;

    // если это сериал, то все проверки должны идти относительно первой доступной серии
    const dataToCheckFrom = details.data; // get(details.data.seasons, [0, 'episodes', 'content', 0], details.data);
    const { visibilityRights, visibilityDetails } = dataToCheckFrom;
    const isSeries = !!dataToCheckFrom.seasons;
    // For series need to use first episode Id as vodAssetId
    const purchaseId = isSeries ? dataToCheckFrom.seasons[0]?.episodes?.content[0]?.id : dataToCheckFrom.id;
    const isSinglePurchase = !isEmpty(intersection(details.data.types, ['EST', 'TVOD']));

    const onSuccess = `${history.location.pathname}${history.location.search}`;

    if (visibilityRights !== 'PLAY' && visibilityDetails === NOT_AUTHENTICATED) {
      if (isSinglePurchase) {
        history.push(`${history.location.pathname}/tariffs`);
        return;
      }
      navigateToAuthPage(`?vodAssetId=${purchaseId}&onSuccess=${onSuccess}`);
      return;
    }

    if (visibilityDetails === MISSING_SUBSCRIPTION) {
      innerAnalyticsVolia.postEvent({
        eventGroup: 'notification',
        eventName: 'unavailable content',
        eventValue: 'show notification',
        addInfo: `vod_asset_id=${dataToCheckFrom.id}; content_type=${get(
          dataToCheckFrom,
          'metadata.contentType',
          'TV_SERIES',
        )}`,
      });

      if (isSinglePurchase) {
        history.push(`${history.location.pathname}/tariffs`);
      } else if (isLifecellUser) {
        // Hack while we don't have react-query flow
        const onSuccess = async () => {
          await resetUserData();
          reloadData();
        };
        showLifecellPaymentModal(
          { id: purchaseId, assetType: 'vodAssetId', onSuccess },
          { sectionToFocus: '@WATCH_CONTROLS' },
        );
      } else {
        history.push({
          pathname: '/profile/packages/filter',
          search: `?vodAssetId=${purchaseId}&onSuccess=${onSuccess}`,
        });
      }
      return;
    }

    //TODO-Inna пересмотреть логику запроса РК и данных для неё в следующем МР
    let item = details.data;
    //если это сериал, но его ещё не смотрели, то открываем с первой серии
    if (item.seasonIds && !item.userVodAssetUrl) {
      item = get(details.data.seasons, [0, 'episodes', 'content', 0], {});
    }

    if (isParentalEnabled && isPgPinNeeded(get(details.data, ['pgRating']), age)) {
      innerAnalyticsVolia.postEvent({
        eventGroup: 'notification',
        eventName: 'PIN request',
        eventValue: 'show notification',
      });
      showEnterPinModal({ sectionToFocus: '@WATCH_CONTROLS' }, () => navigateToPlayerPage({ item }));
      return;
    }

    navigateToPlayerPage({ item });
  };

  onTrailer = () => {
    const { details, navigateToPlayerPage, isParentalEnabled, age, showEnterPinModal } = this.props;

    if (isParentalEnabled && isPgPinNeeded(get(details.data, ['pgRating']), age)) {
      innerAnalyticsVolia.postEvent({
        eventGroup: 'notification',
        eventName: 'PIN request',
        eventValue: 'show notification',
      });
      showEnterPinModal({ sectionToFocus: '@WATCH_CONTROLS' }, () =>
        navigateToPlayerPage({ item: details.data, type: PLAYER_TYPES.TRAILER }),
      );
      return;
    }

    navigateToPlayerPage({ item: details.data, type: PLAYER_TYPES.TRAILER });
  };

  onEpisodesEnter = () => {
    const { history, details } = this.props;

    innerAnalyticsVolia.postEvent({
      eventGroup: 'VOD description page',
      eventName: 'play',
      addInfo: `vod_asset_id=${details.data.id}; content_type=EPISODE`,
    });

    history.push(`/seasons/${CONTENT_TYPES.TV_SERIES}/${details.data.id}`);
  };

  onWatchList = () => {
    const { toggleVodObjectFavoriteStatus, navigateToAuthPage, details, isLoggedIn, history } = this.props;
    const isSeries = !!details.data.seasons;
    const purchaseId = isSeries ? details.data.seasons[0]?.episodes?.content[0]?.id : details.data.id;

    if (!isLoggedIn) {
      const onSuccess = `${history.location.pathname}${history.location.search}`;
      navigateToAuthPage(`?vodAssetId=${purchaseId}&onSuccess=${onSuccess}`);
      return;
    }

    const eventName = details.data.favorite ? 'remove from' : 'add to';
    const assetType = get(details.data.metadata, ['contentType'], CONTENT_TYPES.TV_SERIES);
    const addInfoMovies = `vod_asset_id=${details.data.id};channel_id=null;content_type=MOVIE;type=VOD_ASSET;epgEventState=null`;
    const addInfoSeries = `vod_asset_id=${details.data.id};channel_id=null;content_type=MOVIE;type=TV_SERIES;epgEventState=null`;

    const payloadData = { id: parseInt(details.data.id, 10) };
    if (assetType === CONTENT_TYPES.TV_SERIES) {
      payloadData.seriesId = parseInt(details.data.id, 10);
    }
    innerAnalyticsVolia.postEvent({
      eventGroup: 'VOD description page',
      eventName: `${eventName} favorites`,
      addInfo: assetType === CONTENT_TYPES.TV_SERIES ? addInfoSeries : addInfoMovies,
    });

    toggleVodObjectFavoriteStatus(payloadData);
  };

  onWatchControlEnter = ({ type, id }) => {
    if (['play', 'cover'].includes(type)) this.onPlay();
    if (type === 'watchlist') this.onWatchList();
    if (type === 'episodes') this.onEpisodesEnter();
    if (type === 'trailer') this.onTrailer();

    this.saveElementIdAsLastFocused(id);
  };

  onSectionItemFocus = (pageOffset) => this.setState({ pageOffset });

  render() {
    const { pageOffset, noAnimation } = this.state;
    const { details, lastFocus, reloadData, history } = this.props;
    const { isOnPurchaseRequest = false } = details;

    if (details.error) {
      return (
        <EmptyDataStub
          className={'video-error'}
          emptyTitle={i18n.t('detailsVOD.errorTitle')}
          emptyPlaceholder={i18n.t('detailsVOD.errorMessage')}
          icon={<ErrorIcon width={192} height={192} />}
          buttonText={i18n.t('detailsVOD.errorButton')}
          onEnter={history.goBack}
        />
      );
    }

    return (
      <DetailsWrapper>
        <DetailsInnerWrapper noAnimation={noAnimation} pageOffset={-pageOffset}>
          <MainContentSection
            data={details.data}
            isOnPurchaseRequest={isOnPurchaseRequest}
            noAnimation={noAnimation}
            pageOffset={pageOffset}
            baseFocusOffset={BASE_MAIN_SECTION_OFFSET}
            onWatchControlEnter={this.onWatchControlEnter}
            onItemFocus={this.onSectionItemFocus}
          />
          {details.data && (
            <ExtraContentSection
              data={details.data}
              isLoading={details.isLoading}
              baseFocusOffset={BASE_EXTRA_SECTION_OFFSET}
              onItemFocus={this.onSectionItemFocus}
              reloadData={reloadData}
              lastFocus={lastFocus}
            />
          )}
        </DetailsInnerWrapper>
      </DetailsWrapper>
    );
  }
}

export default compose(withScrollHack, withMouseControls)(Details);
